import { useEffect, useId, useState, type CSSProperties } from "react";
import { ISSUE_TYPES, submitIssue, type IssueType } from "../lib/api";
import { useDialogA11y } from "../lib/useDialogA11y";

interface Props {
  open: boolean;
  drinkId: string;
  drinkName: string;
  versionLabel: string | null;
  defaultStationLabel: string | null;
  onClose: () => void;
  onSubmitted: (msg: string) => void;
}

const MIN_NOTE = 5;

/**
 * Bartender Issue Report modal — opens from RecipeDetail "Report issue" button.
 * Captures: type, note (≥5 chars), station label (defaults to caller's first
 * name). Submitting fires bar-submit-issue, which inserts the row, fans out
 * SMS to every bar_owner, and returns counts. We surface "Sent — Travis was
 * notified" so the bartender knows it landed.
 */
export default function IssueModal({
  open,
  drinkId,
  drinkName,
  versionLabel,
  defaultStationLabel,
  onClose,
  onSubmitted,
}: Props) {
  const titleId = useId();
  const noteId = useId();
  const typeId = useId();
  const stationId = useId();

  const [issueType, setIssueType] = useState<IssueType>("Recipe wrong");
  const [note, setNote] = useState("");
  const [stationLabel, setStationLabel] = useState(defaultStationLabel ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { dialogRef } = useDialogA11y(open, onClose, !busy);

  useEffect(() => {
    if (open) {
      setIssueType("Recipe wrong");
      setNote("");
      setStationLabel(defaultStationLabel ?? "");
      setBusy(false);
      setError(null);
    }
  }, [open, defaultStationLabel]);

  if (!open) return null;

  async function submit() {
    const trimmed = note.trim();
    const trimmedName = stationLabel.trim();
    if (trimmedName.length === 0) {
      setError("Please enter the name of who is submitting this report.");
      return;
    }
    if (trimmed.length < MIN_NOTE) {
      setError(`Note must be at least ${MIN_NOTE} characters.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await submitIssue({
        drink_id: drinkId,
        issue_type: issueType,
        note: trimmed,
        station_label: trimmedName,
      });
      const delivered = res.sms?.delivered ?? 0;
      const targets = res.sms?.targets ?? 0;
      onSubmitted(
        delivered > 0
          ? `Sent — ${delivered} owner${delivered === 1 ? "" : "s"} notified.`
          : targets === 0
            ? "Sent — admins will see it on the next refresh."
            : "Sent — SMS pending (owners will see it shortly).",
      );
      onClose();
    } catch (err: any) {
      setError(err?.message ?? "Could not submit");
      setBusy(false);
    }
  }

  return (
    <div style={styles.backdrop} onClick={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={styles.dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} style={styles.title}>Report issue</h2>
        <p style={styles.body}>
          <strong>{drinkName}</strong>
          {versionLabel ? ` · ${versionLabel}` : ""}
        </p>

        <label htmlFor={typeId} style={styles.label}>What's the problem?</label>
        <select
          id={typeId}
          value={issueType}
          onChange={(e) => setIssueType(e.target.value as IssueType)}
          style={styles.input}
          disabled={busy}
        >
          {ISSUE_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <label htmlFor={noteId} style={styles.label}>
          Note <span style={styles.req}>(required, ≥{MIN_NOTE} chars)</span>
        </label>
        <textarea
          id={noteId}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Describe what you saw — be specific so the manager can act on it."
          style={styles.textarea}
          rows={4}
          autoFocus
          disabled={busy}
        />
        <p style={styles.charCount}>
          {note.trim().length}/{MIN_NOTE}
        </p>

        <label htmlFor={stationId} style={styles.label}>
          Your name / station <span style={styles.req}>(required)</span>
        </label>
        <input
          id={stationId}
          type="text"
          value={stationLabel}
          onChange={(e) => setStationLabel(e.target.value)}
          placeholder='e.g. "Travis Gensler" or "Library Bar — Tablet 1"'
          style={styles.input}
          disabled={busy}
          required
          aria-required="true"
        />

        {error && <div style={styles.error} role="alert">{error}</div>}

        <div style={styles.actions}>
          <button type="button" onClick={onClose} disabled={busy} style={styles.cancelBtn}>
            Cancel
          </button>
          {/* Match ApproveModal pattern: don't silently disable the gold
              submit button on validation gaps — fade visually and let the
              click fall through to submit() which surfaces the inline
              error. Disabled only on `busy` so we never double-submit. */}
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            style={{
              ...styles.submitBtn,
              ...(note.trim().length < MIN_NOTE || stationLabel.trim().length === 0
                ? { opacity: 0.5, cursor: "not-allowed" }
                : {}),
            }}
          >
            {busy ? "Submitting…" : "Submit issue"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 99,
  },
  dialog: {
    width: "100%",
    maxWidth: 480,
    maxHeight: "90vh",
    overflowY: "auto",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    margin: "0 0 8px",
    color: "var(--text)",
  },
  body: { margin: "0 0 12px", fontSize: 13, color: "var(--muted)", lineHeight: 1.5 },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    marginTop: 12,
    marginBottom: 6,
  },
  req: { color: "var(--gold)", textTransform: "none", letterSpacing: 0 },
  input: {
    width: "100%",
    boxSizing: "border-box",
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    fontSize: 16,
    fontFamily: "inherit",
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    fontSize: 16,
    fontFamily: "inherit",
    resize: "vertical",
  },
  charCount: {
    margin: "4px 0 0",
    fontSize: 11,
    color: "var(--muted)",
    textAlign: "right",
  },
  error: {
    marginTop: 10,
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  actions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 16,
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  submitBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
};
